import { typographyCss } from '@creditornot/cb-ingredients';
import { getCookieName } from '@creditornot/cwc-cookie-sdk';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { useCookie } from 'react-use';
import styled from 'styled-components';

import { patchInboundMerchant } from 'apis/merchant-onboarding-service/api';

import { MOBILE_BREAKPOINT } from '../../../../common/hooks/useIsMobile';
import { Typography as T } from '../../../../common/typography';
import { useRegisterTelemetryEvent } from '../../../../core/telemetry/hooks/useRegisterTelemetryEvent';
import { useCookieConsents } from '../../../cookie-consents/CookieConsentsProvider';
import { DEFAULT_WEEKLY_SCHEDULE } from '../../common/WeeklyScheduleInput/constants';
import { SelfServeStepsIdentifiers } from '../constants';
import OpeningHoursForm from '../forms/OpeningHoursForm';
import { apiWeeklyScheduleToOpenningHours, openingHoursToApi } from '../forms/transformer';
import { OpeningHours, OpeningHoursValidationSchema } from '../forms/validation';
import { validateStepParam } from '../helpers';
import { InboundMerchant } from '../types';

type VenueOpenningHoursProps = {
  inboundMerchant: InboundMerchant;
  goToNextStep: () => void;
  setUpdateIsInProgress: (inProgress: boolean) => void;
};

const Wrapper = styled.div``;

const ContentContainer = styled.div`
  margin-block-end: 32px;
`;

const Title = styled(T.Heading6)`
  margin-block-start: 32px;
  padding-block-end: 16px;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Title1()}
  }
`;

const VenueOpeningHoursForm = ({ inboundMerchant, goToNextStep, setUpdateIsInProgress }: VenueOpenningHoursProps) => {
  const { formatMessage } = useIntl();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);

  const register = useRegisterTelemetryEvent();
  const {
    consents: { analytics: analyticsConsent, marketing: marketingConsent },
  } = useCookieConsents();
  const [woltUid] = useCookie(getCookieName('wolt-uid'));
  const { country } = inboundMerchant;

  const handleOpeningHoursSubmit = async (values: OpeningHours): Promise<{ [key: string]: string | Error } | void> => {
    try {
      setUpdateIsInProgress(false);
      const validFormValues = OpeningHoursValidationSchema.validateSync(values);
      const stepParam = validateStepParam(searchParams.get('step'));
      const transformedValues = {
        businessType: 'restaurant',
        venues: [
          {
            ...inboundMerchant.venues[0],
            productLine: 'restaurant',
            openingTimes: openingHoursToApi(validFormValues),
          },
        ],
      };

      register({
        event: 'venue_opening_hours_submitted',
        marketingConsent,
        analyticsConsent,
        properties: {
          __woltUid: woltUid,
          country,
        },
      });

      await patchInboundMerchant({ ...transformedValues, ssmoStepCompletionId: stepParam }, inboundMerchant.merchantId);
      goToNextStep();
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    } finally {
      setUpdateIsInProgress(false);
    }
  };

  return (
    <Wrapper>
      <ContentContainer>
        <Title>{formatMessage({ id: 'venue-details.opening-times' })}</Title>
        <OpeningHoursForm
          handleSubmit={handleOpeningHoursSubmit}
          formId={SelfServeStepsIdentifiers.OpeningHours}
          weeklySchedule={
            inboundMerchant.venues.length && inboundMerchant.venues[0].openingTimes
              ? apiWeeklyScheduleToOpenningHours(inboundMerchant.venues[0].openingTimes)
              : { openingHours: DEFAULT_WEEKLY_SCHEDULE }
          }
        />
      </ContentContainer>
    </Wrapper>
  );
};

export default VenueOpeningHoursForm;
